import React, { useContext } from "react";
import { Photo, PhotoGridProps, ThumbnailSelectionMode } from "./types";
import { EnhancedThumbnail } from "./ImageThumbnail2";
import { EventGalleryContext } from "./index";
import { OrgDownloadContext } from "../OrganizationPhotos/OrgDownloadContext";
import { FindPhotosGalleryContext } from "../MasterImageSearch/components/results/PhotosContainer";

const MemoizedCell: React.FC<{
  columnIndex: number;
  rowIndex: number;
  style: React.CSSProperties;
  data: {
    items: Photo[];
    numColumns: number;
    areMoreItemsLoading: boolean;
    thumbnailProps: PhotoGridProps["otherProps"]["thumbnailProps"] & {
      primaryColor?: string;
      securedOrgView?: boolean;
      disableSelectIcon?: boolean;
      canShowGuestEmailModal?: (photo: any) => boolean;
    };
  };
}> = React.memo(
  ({ columnIndex, rowIndex, style, data }) => {
    const { items, numColumns, thumbnailProps } = data;
    const index = rowIndex * numColumns + columnIndex;
    const item = items[index];
    const context = useContext(EventGalleryContext);
    const orgDownloadContext = useContext(OrgDownloadContext);
    const findPhotosContext = useContext(FindPhotosGalleryContext);

    // Check if direct download is enabled from any context
    const directDownloadEnabled =
      context?.directDownloadEnabled ||
      orgDownloadContext?.directDownloadEnabled ||
      findPhotosContext?.directDownloadEnabled ||
      false;

    // Function to check if we should show the download selector
    const shouldShowDownloadSelect = () => {
      return (
        (!context?.curationMode &&
          context?.event?.price === 0 &&
          directDownloadEnabled) ||
        (!!orgDownloadContext && directDownloadEnabled)
      );
    };

    // Function to handle download image selection
    const handleDownloadImageSelection = ({
      photo,
      index
    }: {
      photo: any;
      index?: number;
    }) => {
      if (orgDownloadContext) {
        const isPhotoInDownloadList =
          orgDownloadContext.downloadableContent?.includes(photo.id);

        if (isPhotoInDownloadList) {
          orgDownloadContext.removeContentFromDownload(photo);
        } else {
          orgDownloadContext.addContentToDownload(photo);
        }
      } else if (context) {
        context.getPhotoId(photo, index, items);
      }
    };

    if (!item) {
      return null;
    }

    return (
      <div style={style} className="photo-cell">
        <EnhancedThumbnail
          photo={item}
          index={index}
          onClick={thumbnailProps.onClick}
          onThumbnailSelect={thumbnailProps.onThumbnailSelect}
          selectionMode={thumbnailProps.selectionMode}
          primaryColor={thumbnailProps.primaryColor}
          securedOrgView={thumbnailProps.securedOrgView}
          disableSelectIcon={thumbnailProps.disableSelectIcon}
          directDownloadEnabled={directDownloadEnabled}
          shouldShowDownloadSelect={shouldShowDownloadSelect}
          handleDownloadImageSelection={handleDownloadImageSelection}
        />
      </div>
    );
  },
  (prev, next) => {
    const prevIndex = prev.rowIndex * prev.data.numColumns + prev.columnIndex;
    const nextIndex = next.rowIndex * next.data.numColumns + next.columnIndex;

    const prevPhoto = prev.data.items[prevIndex];
    const nextPhoto = next.data.items[nextIndex];

    // Don't re-render if the photo and its selection state haven't changed
    return (
      prevPhoto?.id === nextPhoto?.id &&
      prevPhoto?.isSelected === nextPhoto?.isSelected &&
      prev.style === next.style &&
      prev.data.thumbnailProps.selectionMode ===
        next.data.thumbnailProps.selectionMode
    );
  }
);

export default MemoizedCell;
